import React, { useEffect, useMemo, useState } from "react";
import {
  Row,
  Col,
  Card,
  Alert,
  Button,
  Badge,
  Accordion,
} from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import * as XLSX from "xlsx";
import Loading from "../../../../../components/general/Loading";
import {
  handleError,
  isNotBusinessError,
} from "../../../../../utils/handleError";
import {
  getIndicatorComplementaryAmounts,
  getIndicatorComplementaryActivitiesStates,
  getIndicatorAdvancePayments,
} from "../../../../../rest/indicator";
import { currencyFormat } from "../../../../../utils/CurrencyUtils";
import ModalReferences from "./ModalReferences";
import ExportDialog from "../utils/ExportDialog";
import LastUpdate from "../utils/LastUpdate";
import { buildWorkbookComplementary } from "./exporters/exporterIndicatorComplementaryActivities";
import KpiStat from "../utils/KpiStat";
import { useClientPdf } from "../utils/useClientPdf";

export default function IndicatorComplementaryActivities() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [montantes, setMontantes] = useState([]);
  const [estados, setEstados] = useState([]);
  const [adiantamentos, setAdiantamentos] = useState([]);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [refreshVersion, setRefreshVersion] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [referenciasSelecionadas, setReferenciasSelecionadas] = useState([]);
  const [showExport, setShowExport] = useState(false);

  const { pdfRef, exportPdf } = useClientPdf({
    marginMm: 10,
    orientation: "portrait",
    hideSelectors: ['[data-hide-on-export="true"]', ".modal", ".toast"],
  });

  const abrirModalReferencias = (refs) => {
    setReferenciasSelecionadas(refs || []);
    setShowModal(true);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const [resMontantes, resEstados, resAdiantamentos] = await Promise.all([
          getIndicatorComplementaryAmounts(),
          getIndicatorComplementaryActivitiesStates(),
          getIndicatorAdvancePayments(),
        ]);

        const mPayload = resMontantes?.data ?? {};
        const mList = Array.isArray(mPayload.data)
          ? mPayload.data
          : Array.isArray(mPayload.results)
          ? mPayload.results
          : Array.isArray(resMontantes?.data)
          ? resMontantes.data
          : [];
        const mNorm = mList.map((r) => ({
          year: Number(r?.year ?? r?.ano ?? 0),
          type: r?.type ?? r?.tipo ?? "",
          value: Number(r?.value ?? r?.valueSum ?? r?.valor ?? 0),
          onlineValue: Number(
            r?.onlineValue ?? r?.onlineValueSum ?? r?.valorOnline ?? 0
          ),
        }));
        setMontantes(mNorm);

        const ePayload = resEstados?.data ?? {};
        const eList = Array.isArray(ePayload.data)
          ? ePayload.data
          : Array.isArray(ePayload.results)
          ? ePayload.results
          : Array.isArray(resEstados?.data)
          ? resEstados.data
          : [];
        const eNorm = eList.map((r) => ({
          state: r?.state ?? r?.estado ?? "",
          total: Number(r?.total ?? 0),
          processReferences: Array.isArray(r?.processReferences)
            ? r.processReferences
            : [],
        }));
        setEstados(eNorm);

        const aPayload = resAdiantamentos?.data ?? {};
        const aList = Array.isArray(aPayload.data)
          ? aPayload.data
          : Array.isArray(aPayload.results)
          ? aPayload.results
          : Array.isArray(resAdiantamentos?.data)
          ? resAdiantamentos.data
          : [];
        const aNorm = aList.map((r) => ({
          year: Number(r?.year ?? r?.ano ?? 0),
          valorAdiantamento: Number(
            r?.valorAdiantamento ?? r?.valorAdiantamentoSum ?? 0
          ),
          valorRemanescente: Number(
            r?.valorRemanescente ?? r?.valorRemanescenteSum ?? 0
          ),
          valorARestituir: Number(
            r?.valorARestituir ?? r?.valorARestituirSum ?? 0
          ),
        }));
        setAdiantamentos(aNorm);

        const pickTs = (p) =>
          p?.lastRefreshedAt ?? p?.lastUpdated
            ? new Date(p.lastRefreshedAt ?? p.lastUpdated).getTime()
            : null;
        const timestamps = [
          { ts: pickTs(mPayload), ver: mPayload.refreshVersion ?? null },
          { ts: pickTs(ePayload), ver: ePayload.refreshVersion ?? null },
          { ts: pickTs(aPayload), ver: aPayload.refreshVersion ?? null },
        ].filter((x) => x.ts);
        if (timestamps.length > 0) {
          const latest = timestamps.reduce((a, b) => (a.ts >= b.ts ? a : b));
          setLastUpdate(new Date(latest.ts).toISOString());
          setRefreshVersion(latest.ver);
        }
      } catch (err) {
        if (isNotBusinessError(err)) setError(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const round2 = (n) =>
    Math.round((Number(n || 0) + Number.EPSILON) * 100) / 100;

  const typeLabel = {
    BOOTCAMP: "Bootcamp",
    DIDACTIC: "Didático",
    DISCLOSURE: "Divulgação",
  };

  const stateLabel = {
    APPROVED: "Aprovado",
    REJECTED: "Rejeitado",
    SUBMITTED: "Submetido",
    WAIT_CONTESTATION: "A aguardar contestação",
  };

  const stateColor = {
    APPROVED: "success",
    REJECTED: "danger",
    SUBMITTED: "primary",
    WAIT_CONTESTATION: "warning",
  };

  const montantesByYear = useMemo(() => {
    const map = new Map();
    for (const m of montantes) {
      const year = m?.year;
      if (!map.has(year))
        map.set(year, {
          year,
          rows: [],
          totalSolic: 0,
          totalAprov: 0,
        });
      const bucket = map.get(year);
      const vSolic = Number(m?.value ?? 0);
      const vAprov = Number(m?.onlineValue ?? 0);
      bucket.rows.push({
        type: m?.type,
        typeLabel: typeLabel[m?.type] || m?.type,
        solicitado: vSolic,
        aprovado: vAprov,
      });
      bucket.totalSolic += vSolic;
      bucket.totalAprov += vAprov;
    }
    const arr = Array.from(map.values()).sort((a, b) => a.year - b.year);
    arr.forEach((y) =>
      y.rows.sort((a, b) => a.typeLabel.localeCompare(b.typeLabel))
    );
    return arr;
  }, [montantes]);

  const montantesKpis = useMemo(() => {
    const totalSolicitado = montantes.reduce(
      (s, m) => s + Number(m?.value ?? 0),
      0
    );
    const totalAprovado = montantes.reduce(
      (s, m) => s + Number(m?.onlineValue ?? 0),
      0
    );
    const taxa =
      totalSolicitado > 0 ? (totalAprovado / totalSolicitado) * 100 : 0;
    return { totalSolicitado, totalAprovado, taxa };
  }, [montantes]);

  const adiantKpis = useMemo(() => {
    const adiantado = adiantamentos.reduce(
      (s, a) => s + Number(a?.valorAdiantamento ?? 0),
      0
    );
    const remanescente = adiantamentos.reduce(
      (s, a) => s + Number(a?.valorRemanescente ?? 0),
      0
    );
    const aRestituir = adiantamentos.reduce(
      (s, a) => s + Number(a?.valorARestituir ?? 0),
      0
    );
    return { adiantado, remanescente, aRestituir };
  }, [adiantamentos]);

  const hasMontantes = montantesByYear.length > 0;
  const hasEstados = estados.length > 0;
  const hasAdiantamentos = adiantamentos.length > 0;
  const hasAnyData = hasMontantes || hasEstados || hasAdiantamentos;

  const barWrap = {
    height: 6,
    background: "#eef1f4",
    borderRadius: 999,
    overflow: "hidden",
  };
  const bar = (pct) => ({
    width: `${Math.min(100, Math.max(0, pct))}%`,
    height: "100%",
    background: "#0d6efd",
  });

  const handleExportExcel = async () => {
    try {
      const wb = await buildWorkbookComplementary();
      XLSX.writeFile(wb, "atividades_complementares.xlsx");
      setTimeout(() => setShowExport(false), 0);
    } catch (e) {
      console.error("[Excel AtividadesComplementares] falhou:", e);
      alert("Falha ao gerar Excel: " + (e?.message || e));
    }
  };

  if (loading) return <Loading />;
  if (error) return handleError(error);

  return (
    <div className="container-fluid" ref={pdfRef}>
      <Row className="align-items-center mb-3">
        <Col>
          <h4 className="text-secondary mb-0">
            <FormattedMessage
              id="indicator.menu.complementares"
              defaultMessage="Atividades Complementares"
            />
          </h4>
        </Col>
        <Col className="text-end">
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              if (hasAnyData) setShowExport(true);
            }}
            aria-disabled={!hasAnyData}
            style={{
              color: "#e5533d",
              textDecoration: "underline",
              pointerEvents: hasAnyData ? "auto" : "none",
              opacity: hasAnyData ? 1 : 0.5,
            }}
            data-hide-on-export="true"
          >
            <FormattedMessage
              id="indicator.exportar"
              defaultMessage="Exportar"
            />
          </a>
        </Col>
      </Row>

      <Accordion
        alwaysOpen
        defaultActiveKey={["montantes", "estados", "adiantamentos"]}
        className="mb-3"
      >
        <Accordion.Item eventKey="montantes">
          <Accordion.Header>Montantes</Accordion.Header>
          <Accordion.Body>
            <Row className="g-3 mb-3">
              <Col xs={12} md={4}>
                <KpiStat
                  label="Total Solicitado"
                  value={currencyFormat(round2(montantesKpis.totalSolicitado))}
                />
              </Col>
              <Col xs={12} md={4}>
                <KpiStat
                  label="Total Aprovado"
                  value={currencyFormat(round2(montantesKpis.totalAprovado))}
                />
              </Col>
              <Col xs={12} md={4}>
                <KpiStat
                  label="Taxa de Aprovação"
                  value={`${round2(montantesKpis.taxa).toLocaleString("pt-PT", {
                    maximumFractionDigits: 2,
                  })}%`}
                />
              </Col>
            </Row>

            {!hasMontantes ? (
              <Alert variant="light" className="border">
                <i className="fa-regular fa-circle-info me-2" />
                Sem dados para apresentar.
              </Alert>
            ) : (
              <Row className="g-3">
                {montantesByYear.map((y) => {
                  const taxa =
                    y.totalSolic > 0 ? (y.totalAprov / y.totalSolic) * 100 : 0;
                  const pct = Math.min(100, Math.round(taxa * 10) / 10);
                  return (
                    <Col key={y.year} xs={12} md={6}>
                      <Card className="shadow-sm h-100">
                        <Card.Body className="py-3">
                          <div className="d-flex justify-content-between align-items-baseline mb-2">
                            <div className="h6 mb-0">{y.year}</div>
                            <div className="text-muted">
                              Aprovado:{" "}
                              <strong className="text-body">
                                {currencyFormat(round2(y.totalAprov))}
                              </strong>
                            </div>
                          </div>

                          <div className="mb-3">
                            {y.rows.map((r, i) => (
                              <div
                                key={i}
                                className="d-flex justify-content-between align-items-center py-2"
                                style={{
                                  borderBottom:
                                    i < y.rows.length - 1
                                      ? "1px solid #f3f4f6"
                                      : "none",
                                }}
                              >
                                <div className="text-muted">{r.typeLabel}</div>
                                <div className="d-flex gap-3">
                                  <div className="text-muted">
                                    Solicitado:{" "}
                                    <strong className="text-body">
                                      {currencyFormat(round2(r.solicitado))}
                                    </strong>
                                  </div>
                                  <div className="text-muted">
                                    Aprovado:{" "}
                                    <strong className="text-body">
                                      {currencyFormat(round2(r.aprovado))}
                                    </strong>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>

                          <div className="d-flex justify-content-between align-items-center text-muted mb-1">
                            <span>Taxa de aprovação</span>
                            <strong className="text-body">
                              {round2(pct).toLocaleString("pt-PT", {
                                maximumFractionDigits: 1,
                              })}
                              %
                            </strong>
                          </div>
                          <div style={barWrap}>
                            <div style={bar(pct)} />
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            )}
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="estados">
          <Accordion.Header>Estados</Accordion.Header>
          <Accordion.Body>
            {!hasEstados ? (
              <Alert variant="light" className="border">
                <i className="fa-regular fa-circle-info me-2" />
                Sem dados para apresentar.
              </Alert>
            ) : (
              <Card className="shadow-sm">
                <Card.Body className="py-2">
                  {estados.map((e, idx) => {
                    const refs = e?.processReferences || [];
                    const cor = stateColor[e?.state] || "secondary";
                    return (
                      <div
                        key={idx}
                        className="d-flex flex-wrap justify-content-between align-items-center py-2"
                        style={{
                          borderBottom:
                            idx < estados.length - 1
                              ? "1px solid #f3f4f6"
                              : "none",
                        }}
                      >
                        <div className="d-flex align-items-center gap-2">
                          <Badge bg={cor}>
                            {stateLabel[e?.state] || e?.state}
                          </Badge>
                          <span className="text-muted">
                            Número de EAs:{" "}
                            <strong className="text-body">
                              {e?.total ?? 0}
                            </strong>
                          </span>
                        </div>
                        <div className="text-end">
                          {refs.length === 0 ? (
                            <span className="text-muted">Sem referências</span>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline-primary"
                              onClick={() => abrirModalReferencias(refs)}
                            >
                              Ver referências ({refs.length})
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </Card.Body>
              </Card>
            )}
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="adiantamentos">
          <Accordion.Header>Adiantamentos</Accordion.Header>
          <Accordion.Body>
            <Row className="g-3 mb-3">
              <Col xs={12} md={4}>
                <KpiStat
                  label="Valor Adiantado"
                  value={currencyFormat(round2(adiantKpis.adiantado))}
                />
              </Col>
              <Col xs={12} md={4}>
                <KpiStat
                  label="Valor Remanescente"
                  value={currencyFormat(round2(adiantKpis.remanescente))}
                />
              </Col>
              <Col xs={12} md={4}>
                <KpiStat
                  label="Valor a Restituir"
                  value={currencyFormat(round2(adiantKpis.aRestituir))}
                />
              </Col>
            </Row>

            {!hasAdiantamentos ? (
              <Alert variant="light" className="border">
                <i className="fa-regular fa-circle-info me-2" />
                Sem dados para apresentar.
              </Alert>
            ) : (
              <Row className="g-3 row-cols-1 row-cols-md-2">
                {adiantamentos
                  .slice()
                  .sort((a, b) => a.year - b.year)
                  .map((a, idx) => (
                    <Col key={idx} className="d-flex">
                      <Card className="shadow-sm w-100">
                        <Card.Body className="py-3">
                          <div className="h6 mb-3">{a.year}</div>
                          {[
                            {
                              label: "Valor Adiantado",
                              value: round2(a?.valorAdiantamento),
                            },
                            {
                              label: "Valor Remanescente",
                              value: round2(a?.valorRemanescente),
                            },
                            {
                              label: "Valor a Restituir",
                              value: round2(a?.valorARestituir),
                            },
                          ].map((row, i, arr) => (
                            <div
                              key={row.label}
                              className="d-flex justify-content-between align-items-center text-muted py-2"
                              style={{
                                borderBottom:
                                  i < arr.length - 1
                                    ? "1px solid #f3f4f6"
                                    : "none",
                              }}
                            >
                              <span>{row.label}</span>
                              <strong className="text-body">
                                {currencyFormat(row.value)}
                              </strong>
                            </div>
                          ))}
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
              </Row>
            )}
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>

      <LastUpdate iso={lastUpdate} version={refreshVersion} className="mt-3" />

      <ModalReferences
        show={showModal}
        onHide={() => setShowModal(false)}
        referencias={referenciasSelecionadas}
      />

      <ExportDialog
        show={showExport}
        onHide={() => setShowExport(false)}
        options={[
          {
            onClick: handleExportExcel,
            filename: "atividades_complementares.xlsx",
          },
          {
            onClick: () =>
              exportPdf("indicadores_atividades_complementares.pdf"),
            filename: "indicadores_atividades_complementares.pdf",
          },
        ]}
      />
    </div>
  );
}